/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import type { InputDOMProps, Validation } from '@react-types/shared';
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, type ReactNode, useRef } from 'react';
import { useTextField } from 'react-aria';
import {
  Button as ButtonCore,
  type ContextValue,
  DropZone as DropZoneCore,
  type DropZoneProps as DropZoneCoreProps,
  type DropZoneRenderProps as DropZoneCoreRenderProps,
  FieldErrorContext as FieldErrorContextCore,
  FileTrigger as FileTriggerCore,
  type FileTriggerProps as FileTriggerCoreProps,
  FormContext as FormContextCore,
  Provider,
  TextContext as TextContextCore,
  useContextProps,
  useSlottedContext,
  VisuallyHidden,
} from 'react-aria-components';
import { FileAttachmentStrokeIcon } from '../../icons/file-attachment-stroke/file-attachment-stroke.icon';
import { ImageStrokeIcon } from '../../icons/image-stroke/image-stroke.icon';
import { $ } from '../../tokens.stylex';
import { mergeStyles } from '../../utils/merge-styles/merge-styles.util';
import { HintContext } from '../hint/hint.component';
import { Text } from '../text/text.component';
import { View } from '../view/view.component';

interface UploadBoxProps extends
  Omit<DropZoneCoreProps, 'className' | 'style' | 'children'>,
  Pick<FileTriggerCoreProps,
    'acceptedFileTypes' |
    'allowsMultiple' |
    'defaultCamera' |
    'onSelect' |
    'acceptDirectory'>,
  Omit<Validation<File[]>, 'validationState'>,
  Pick<InputDOMProps, 'name'> {
  value?: File[];
  children?: ReactNode | ((values: DropZoneCoreRenderProps & { isInvalid: boolean; defaultChildren: ReactNode | undefined }) => ReactNode);
  style?: stylex.StyleXStyles;
}

const UploadBoxContext = createContext<ContextValue<UploadBoxProps, HTMLDivElement>>({});

const UploadBox = forwardRef(({ style: styleProp, ...mergedProps }: UploadBoxProps, ref: ForwardedRef<HTMLDivElement>) => {
  [mergedProps, ref] = useContextProps(mergedProps, ref, UploadBoxContext);

  const {
    style: contextStyle,
    acceptedFileTypes,
    allowsMultiple,
    defaultCamera,
    onSelect,
    acceptDirectory,
    isInvalid: isInvalidProp = false,
    name,
    isRequired,
    validate,
    validationBehavior: validationBehaviorProp,
    value = [],
    children,
    ...coreProps
  } = mergedProps as UploadBoxProps;

  const { validationBehavior: formValidationBehavior } = useSlottedContext(FormContextCore) || {};
  const validationBehavior = validationBehaviorProp ?? formValidationBehavior ?? 'native';

  const inputRef = useRef<HTMLInputElement>(null);

  const { inputProps, descriptionProps, errorMessageProps, ...validation } = useTextField({
    value: value.length > 0 ? `${value.length} file(s)` : '',
    validate: validate ? () => validate(value) : undefined,
    isInvalid: isInvalidProp || undefined,
    isRequired,
    validationBehavior,
    name,
    inputElementType: 'input',
    'aria-label': (coreProps as Record<string, unknown>)['aria-label'] as string ?? 'Upload',
  }, inputRef);

  const isInvalid = validation.isInvalid || isInvalidProp;

  const isImageOnly = Boolean(acceptedFileTypes?.length) && acceptedFileTypes?.every(isImageFileType);

  return (
    <DropZoneCore
      ref={ref}
      {...stylex.props(wrapperStyles.base)}
      {...coreProps}>
      {(renderProps) => {
        const isActive = renderProps.isHovered || renderProps.isDropTarget || renderProps.isFocusVisible;

        const colors = getUploadBoxColors({ isDisabled: renderProps.isDisabled, isActive, isInvalid });

        const Icon = isImageOnly ? ImageStrokeIcon : FileAttachmentStrokeIcon;

        const resolvedChildren = typeof children === 'function'
          ? children({ ...renderProps, isInvalid, defaultChildren: undefined })
          : children;

        return (
          <>
            <div {...stylex.props(
              styles.base,
              isInvalid && styles.invalid,
              isActive && !isInvalid && styles.active,
              isActive && isInvalid && styles.activeInvalid,
              renderProps.isDisabled && styles.disabled,
              mergeStyles(contextStyle, styleProp),
            )}>
              <Icon size={20} style={iconStyles.base} color={colors.content} />
              <View>
                <Text size={16} style={textStyles.base} color={colors.content}>Drop or </Text>

                <FileTriggerCore
                  acceptedFileTypes={acceptedFileTypes}
                  allowsMultiple={allowsMultiple}
                  defaultCamera={defaultCamera}
                  onSelect={onSelect}
                  acceptDirectory={acceptDirectory}>
                  <ButtonCore isDisabled={renderProps.isDisabled} {...stylex.props(buttonStyles.base)}>
                    {(buttonRenderProps) =>
                      <Text
                        size={16}
                        style={textStyles.base}
                        color={getUploadBoxColors({
                          isDisabled: renderProps.isDisabled,
                          isActive,
                          isInvalid,
                          isActionFocused: buttonRenderProps.isFocusVisible,
                        }).action}>
                        upload
                      </Text>}
                  </ButtonCore>
                </FileTriggerCore>

                <Text size={16} style={textStyles.base} color={colors.content}> a file</Text>
              </View>
            </div>
            <VisuallyHidden>
              <input ref={inputRef} {...inputProps} />
            </VisuallyHidden>
            {resolvedChildren &&
              <Provider
                values={[
                  [FieldErrorContextCore, validation],
                  [TextContextCore, {
                    slots: {
                      description: descriptionProps,
                      errorMessage: errorMessageProps,
                    },
                  }],
                  [HintContext, {}],
                ]}>
                {resolvedChildren}
              </Provider>
            }
          </>
        );
      }}
    </DropZoneCore>
  );
});

function getUploadBoxColors({
  isDisabled,
  isActive,
  isInvalid,
  isActionFocused = false,
}: {
  isDisabled: boolean;
  isActive: boolean;
  isInvalid: boolean;
  isActionFocused?: boolean;
}) {
  if (isDisabled) {
    return { content: 'disabled', action: 'disabled' } as const;
  }

  if (isInvalid) {
    const content = isActive ? 'primary' : 'tertiary';
    const action = isActionFocused || isActive ? 'critical' : 'tertiary';

    return { content, action } as const;
  }

  const content = isActive ? 'primary' : 'tertiary';
  const action = isActionFocused || isActive ? 'interactive' : 'tertiary';

  return { content, action } as const;
}

function isImageFileType(type: string) {
  const normalized = type.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  if (normalized.startsWith('image/')) {
    return true;
  }

  if (normalized.startsWith('.')) {
    return [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.bmp',
      '.tif',
      '.tiff',
      '.svg',
      '.avif',
      '.heic',
      '.heif',
    ].includes(normalized);
  }
  return false;
};

const wrapperStyles = stylex.create({
  base: {
    gap: 4,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
});

const styles = stylex.create({
  base: {
    padding: 8,
    borderColor: $.border16,
    borderRadius: 8,
    borderStyle: 'dashed',
    borderWidth: 1,
    gap: 8,
    alignItems: 'center',
    alignSelf: "stretch",
    appearance: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    forcedColorAdjust: 'none',
    justifyContent: 'center',
    transitionDuration: '300ms',
    transitionProperty: 'backgroundColor, borderColor',
    height: 88,
    minHeight: 80,
  },
  active: {
    borderColor: $.borderInteractive,
    backgroundColor: $.surfaceHighlightSilent,
  },
  disabled: {
    borderColor: $.border06,
  },
  invalid: {
    borderColor: $.borderCritical,
    borderStyle: 'solid',
    borderWidth: 2,
  },
  activeInvalid: {
    backgroundColor: $.surfaceCriticalSilent,
  },
});

const buttonStyles = stylex.create({
  base: {
    padding: 0,
    borderRadius: 4,
    borderStyle: 'none',
    appearance: 'none',
    backgroundColor: 'transparent',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    outlineColor: $.borderInteractive,
    outlineOffset: '1px',
    outlineStyle: {
      default: 'none',
      ':is([data-focus-visible])': 'solid',
    },
    outlineWidth: '2px',
  }
});

const textStyles = stylex.create({
  base: {
    transitionDuration: '300ms',
    transitionProperty: 'color',
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '300ms',
    transitionProperty: 'stroke, fill',
  }
});

export { UploadBox, UploadBoxContext, type UploadBoxProps };
